<template>
  <div class="file-upload-wrap">
    <!-- 拖拽上传区（达到上限时隐藏） -->
    <div
      v-if="!reachLimit"
      class="file-upload-trigger"
      :class="{ 'is-dragover': isDragover }"
      @click="triggerPick"
      @drop.prevent="onDrop"
      @dragover.prevent="isDragover = true"
      @dragleave.prevent="isDragover = false"
    >
      <input
        ref="inputRef"
        type="file"
        class="hidden"
        :multiple="maxCount > 1"
        :accept="accept || undefined"
        @change="onInputChange"
      />
      <svg class="file-upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.6" d="M7 16a4 4 0 01.88-7.903A5 5 0 1116.9 6L17 6a5 5 0 011 9.9M12 12v9m0-9l-3 3m3-3l3 3"/>
      </svg>
      <div class="file-upload-text">点击或拖拽文件上传</div>
      <div class="file-upload-tip">
        最多 {{ maxCount }} 个，单个不超过 {{ maxSize }}MB{{ accept ? `，仅支持 ${acceptLabel}` : '' }}
      </div>
    </div>

    <!-- 文件列表 -->
    <div v-if="renderList.length" class="file-upload-list">
      <div
        v-for="item in renderList"
        :key="item.uid"
        class="file-upload-item"
      >
        <FileIcon :file="item" :thumb="item.status === 'success'" class="w-9 h-9 shrink-0" />

        <div class="file-upload-main">
          <div class="file-upload-row">
            <span class="file-upload-name" :title="item.name">{{ item.name }}</span>
            <span v-if="item.sizeText" class="file-upload-size">{{ item.sizeText }}</span>
          </div>

          <!-- 状态行 -->
          <div v-if="item.status === 'uploading'" class="file-upload-progress">
            <div class="progress-bar">
              <div class="progress-inner" :style="{ width: item.progress + '%' }"></div>
            </div>
            <span class="progress-text">{{ item.progress }}%</span>
          </div>
          <div v-else-if="item.status === 'error'" class="file-upload-error">
            <span>上传失败</span>
            <button type="button" class="retry-btn" @click.stop="retryUpload(item)">重试</button>
          </div>
          <div v-else-if="item.status === 'success'" class="file-upload-meta">
            <svg class="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
            </svg>
            <span>已上传</span>
          </div>
        </div>

        <!-- 操作按钮 -->
        <button
          type="button"
          class="file-upload-remove"
          title="移除"
          @click.stop="removeItem(item)"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { ElMessage } from 'element-plus'
import FileIcon from './FileIcon.vue'
import { uploadFile } from '@/api/file'
import { getFileName, getFileSize } from '@/utils/fileType'
import { isPreviewableAttachmentFile } from '@/utils/fileAccept'

const props = defineProps({
  files: { type: Array, default: () => [] },
  accept: { type: String, default: '' },
  maxCount: { type: Number, default: 5 },
  maxSize: { type: Number, default: 10 },
  // 上传目录（业务模块），默认 'workflow'
  module: { type: String, default: 'workflow' },
  bucket: { type: String, default: 'private' }
})

const emit = defineEmits(['update:files', 'uploading-change'])

const inputRef = ref(null)
const isDragover = ref(false)
// 上传中的临时项（uid -> item），与已完成 files 分开管理
const uploadingItems = ref([])
const progressMap = ref({})

const reachLimit = computed(() => props.files.length + uploadingItems.value.length >= props.maxCount)

const acceptLabel = computed(() => {
  if (!props.accept) return ''
  return props.accept.split(',').map(s => s.trim().replace(/^\./, '').replace(/\/\*$/, '')).join('、').toUpperCase()
})

const renderList = computed(() => {
  const done = props.files.map((file, index) => {
    const uid = file.uid || file.fileKey || `done-${index}`
    return {
      uid,
      name: getFileName(file),
      sizeText: getFileSize(file),
      status: 'success',
      progress: 100,
      raw: file,
      isFile: true
    }
  })
  const uploading = uploadingItems.value.map(item => ({
    uid: item.uid,
    name: item.name,
    sizeText: item.sizeText,
    status: item.status,
    progress: progressMap.value[item.uid] || 0,
    raw: item.raw,
    isFile: false
  }))
  return [...done, ...uploading]
})

function triggerPick() {
  if (reachLimit.value) {
    ElMessage.warning(`最多上传 ${props.maxCount} 个文件`)
    return
  }
  inputRef.value?.click()
}

function onInputChange(e) {
  const files = Array.from(e.target.files || [])
  handleFiles(files)
  e.target.value = ''
}

function onDrop(e) {
  isDragover.value = false
  if (reachLimit.value) {
    ElMessage.warning(`最多上传 ${props.maxCount} 个文件`)
    return
  }
  const files = Array.from(e.dataTransfer?.files || [])
  handleFiles(files)
}

function handleFiles(files) {
  files.forEach(file => {
    if (props.files.length + uploadingItems.value.length >= props.maxCount) {
      ElMessage.warning(`最多上传 ${props.maxCount} 个文件`)
      return
    }
    if (!isAccepted(file)) {
      ElMessage.warning(`${file.name} 不符合允许的文件类型`)
      return
    }
    if (file.size > props.maxSize * 1024 * 1024) {
      ElMessage.warning(`${file.name} 不能超过 ${props.maxSize}MB`)
      return
    }
    startUpload(file)
  })
}

function isAccepted(file) {
  return isPreviewableAttachmentFile(file, props.accept)
}

async function startUpload(rawFile) {
  const uid = `up-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  const item = {
    uid,
    name: rawFile.name,
    sizeText: getFileSize({ size: rawFile.size }),
    status: 'uploading',
    raw: rawFile
  }
  uploadingItems.value.push(item)
  progressMap.value[uid] = 0
  emitUploading(true)

  // 模拟进度（el-upload 无 onProgress 时也用此策略）
  const fakeTimer = setInterval(() => {
    const cur = progressMap.value[uid] || 0
    if (cur < 90) {
      progressMap.value[uid] = Math.min(90, cur + Math.random() * 15)
    }
  }, 300)

  try {
    const uploadResult = await uploadFile(rawFile, props.module, props.bucket)
    const data = uploadResult?.data || uploadResult || {}
    const fileKey = data.fileName || data.fileKey || data.key
    if (!fileKey) throw new Error('上传结果缺少文件 Key')

    const uploaded = {
      uid,
      name: rawFile.name,
      fileName: rawFile.name,
      originalName: data.originalName || rawFile.name,
      fileKey,
      bucket: data.bucket || props.bucket,
      size: rawFile.size,
      fileSize: rawFile.size,
      contentType: rawFile.type,
      url: data.url
    }
    progressMap.value[uid] = 100
    emit('update:files', [...props.files, uploaded])

    // 移除上传中项
    uploadingItems.value = uploadingItems.value.filter(i => i.uid !== uid)
    if (!uploadingItems.value.length) emitUploading(false)
  } catch (err) {
    const target = uploadingItems.value.find(i => i.uid === uid)
    if (target) target.status = 'error'
    ElMessage.error(err?.message || '附件上传失败')
  } finally {
    clearInterval(fakeTimer)
  }
}

async function retryUpload(item) {
  // 找到原始 file 对象（浏览器 File 缓存在 raw 字段）
  const raw = item.raw
  if (!raw) {
    ElMessage.warning('请重新选择文件')
    uploadingItems.value = uploadingItems.value.filter(i => i.uid !== item.uid)
    return
  }
  uploadingItems.value = uploadingItems.value.filter(i => i.uid !== item.uid)
  startUpload(raw)
}

function removeItem(item) {
  if (item.status === 'success' && item.isFile) {
    // 已完成：从 files 中移除
    const next = props.files.filter((_, idx) => {
      const uid = props.files[idx].uid || props.files[idx].fileKey || `done-${idx}`
      return uid !== item.uid
    })
    emit('update:files', next)
  } else {
    // 上传中 / 失败：从 uploadingItems 中移除
    uploadingItems.value = uploadingItems.value.filter(i => i.uid !== item.uid)
    if (!uploadingItems.value.length) emitUploading(false)
  }
}

let uploadingEmitted = false
function emitUploading(v) {
  if (uploadingEmitted === v) return
  uploadingEmitted = v
  emit('uploading-change', v)
}

defineExpose({
  // 当前是否正在上传
  isUploading: () => uploadingItems.value.length > 0
})
</script>

<style scoped>
.file-upload-wrap {
  width: 100%;
}

.file-upload-trigger {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  border: 1.5px dashed #cbd5e1;
  border-radius: 10px;
  background: #f8fafc;
  cursor: pointer;
  transition: border-color .15s, background .15s;
  text-align: center;
}
.file-upload-trigger:hover {
  border-color: #2563eb;
  background: #eff6ff;
}
.file-upload-trigger.is-dragover {
  border-color: #2563eb;
  background: #dbeafe;
}
.file-upload-icon {
  width: 28px;
  height: 28px;
  color: #94a3b8;
  margin-bottom: 6px;
}
.file-upload-trigger:hover .file-upload-icon {
  color: #2563eb;
}
.file-upload-text {
  font-size: 13px;
  color: #475569;
  font-weight: 500;
}
.file-upload-tip {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 4px;
}

.file-upload-list {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.file-upload-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  transition: border-color .15s, background .15s;
}
.file-upload-item:hover {
  border-color: #cbd5e1;
  background: #f8fafc;
}
.file-upload-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.file-upload-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.file-upload-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 500;
  color: #0f172a;
}
.file-upload-size {
  flex: 0 0 auto;
  font-size: 12px;
  color: #94a3b8;
}
.file-upload-progress {
  display: flex;
  align-items: center;
  gap: 8px;
}
.progress-bar {
  flex: 1;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;
}
.progress-inner {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #2563eb);
  transition: width .3s ease;
}
.progress-text {
  font-size: 12px;
  color: #2563eb;
  font-variant-numeric: tabular-nums;
  min-width: 32px;
  text-align: right;
}
.file-upload-error {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #dc2626;
}
.retry-btn {
  font-size: 12px;
  color: #2563eb;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  cursor: pointer;
}
.retry-btn:hover {
  background: #dbeafe;
}
.file-upload-meta {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #16a34a;
}
.file-upload-remove {
  flex: 0 0 auto;
  padding: 6px;
  color: #94a3b8;
  border-radius: 6px;
  cursor: pointer;
  transition: color .15s, background .15s;
}
.file-upload-remove:hover {
  color: #dc2626;
  background: #fef2f2;
}
</style>
